import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { NavigationDashboardTabItemDto } from '@asset-metrix/portal-gateway-client-js';
import {
  Button,
  getTopNavigationLinkContent,
  GoogleFontIcon,
  type NavigationItemData,
  TopNavigation as PortalUiTopNavigation,
} from '@asset-metrix/portal-ui';

import {
  useDashboardEditModeStore,
  useIsDashboardDraftDirty,
} from '../../store/states/dashboardEditMode';
import { AllDashboardsModal } from '../customizableDashboard/AllDashboardsModal';
import { CreateDashboardModal } from '../customizableDashboard/CreateDashboardModal';
import { DashboardUnsavedChangesModal } from '../customizableDashboard/DashboardUnsavedChangesModal';
import { usePageDashboardsQuery } from '../customizableDashboard/usePageDashboardsQuery';

import { useDashboardUrlState } from './useDashboardUrlState';

type DashboardTopNavigationProps = {
  sourcePageId: string;
  dashboardNavItems: NavigationDashboardTabItemDto[] | undefined;
};

export function DashboardTopNavigation({
  sourcePageId,
  dashboardNavItems,
}: DashboardTopNavigationProps) {
  const { t } = useTranslation();
  const navItems = dashboardNavItems ?? [];
  const {
    activeDashboardId,
    activeDashboardSlug,
    setActiveDashboardById,
    setActiveDashboardSlug,
  } = useDashboardUrlState(navItems);

  const [createOpen, setCreateOpen] = useState(false);
  const [allOpen, setAllOpen] = useState(false);
  const [pendingSlug, setPendingSlug] = useState<string | null>(null);

  const isDirty = useIsDashboardDraftDirty(activeDashboardId);
  const isSaving = useDashboardEditModeStore((state) => state.isSaving);
  const exitDashboardEdit = useDashboardEditModeStore(
    (state) => state.exitDashboardEdit
  );
  const pendingDashboardModal = useDashboardEditModeStore(
    (state) => state.pendingDashboardModal
  );
  const clearPendingDashboardModal = useDashboardEditModeStore(
    (state) => state.clearPendingDashboardModal
  );
  const navigationLocked = isSaving;

  // Empty-state CTAs in Main set pendingDashboardModal. This tree stays mounted
  // on customizable pages even when there are no pinned tabs, so Create / All
  // Dashboards can still open from those empty states.
  useEffect(() => {
    if (pendingDashboardModal === 'createDashboard') {
      setCreateOpen(true);
      clearPendingDashboardModal();
    } else if (pendingDashboardModal === 'allDashboards') {
      setAllOpen(true);
      clearPendingDashboardModal();
    }
  }, [clearPendingDashboardModal, pendingDashboardModal]);

  const { data: pageDashboards } = usePageDashboardsQuery(sourcePageId);

  const tabItems: NavigationItemData[] = navItems.map((item) => ({
    label: item.label,
    completeSlug: item.slug,
    dashboardId: item.dashboardId,
  }));

  const activeColumnIndex = navItems.findIndex(
    (item) => item.slug === activeDashboardSlug
  );

  const dashboardCount = pageDashboards?.length ?? 0;

  const navigateToSlug = (slug: string) => {
    void setActiveDashboardSlug(slug);
  };

  const requestNavigateToSlug = (slug: string) => {
    if (navigationLocked) {
      return;
    }
    if (slug === activeDashboardSlug) {
      return;
    }
    if (isDirty) {
      setPendingSlug(slug);
      return;
    }
    navigateToSlug(slug);
  };

  const trailingActions = (
    <>
      <Button
        title={t('app.dashboard.topNavigation.addDashboard')}
        variant="secondary"
        disabled={navigationLocked}
        onClick={() => setCreateOpen(true)}
      >
        <GoogleFontIcon name="add" />
      </Button>
      <Button
        title={t('app.dashboard.topNavigation.allDashboards', {
          count: dashboardCount,
        })}
        icon={<GoogleFontIcon name="folder" small />}
        variant="secondary"
        disabled={navigationLocked}
        onClick={() => setAllOpen(true)}
      >
        {dashboardCount > 0 ? (
          <span data-testid="all-dashboards-count-badge">{dashboardCount}</span>
        ) : undefined}
      </Button>
    </>
  );

  return (
    <>
      <PortalUiTopNavigation
        activeColumnIndex={
          activeColumnIndex !== -1 ? activeColumnIndex : undefined
        }
        activeSlug={activeDashboardSlug ?? ''}
        navigation={tabItems}
        trailingActions={trailingActions}
        Link={(item) => {
          const linkContent = getTopNavigationLinkContent({
            activeSlug: activeDashboardSlug ?? '',
            item,
            navigation: tabItems,
          });
          return (
            // Dashboard tabs update ?dashboard= instead of navigating to a new route.
            // eslint-disable-next-line jsx-a11y/anchor-is-valid -- matches portal-ui TopNavigation default link
            <a
              href="#"
              aria-disabled={navigationLocked || undefined}
              className={linkContent.className}
              onClick={(event) => {
                event.preventDefault();
                requestNavigateToSlug(item.completeSlug);
              }}
              style={
                navigationLocked
                  ? { pointerEvents: 'none', opacity: 0.5 }
                  : undefined
              }
            >
              {linkContent.children}
            </a>
          );
        }}
      />
      <CreateDashboardModal
        onClose={() => setCreateOpen(false)}
        onCreated={(dashboardSlug) => {
          setCreateOpen(false);
          void setActiveDashboardSlug(dashboardSlug);
        }}
        opened={createOpen}
        sourcePageId={sourcePageId}
      />
      <AllDashboardsModal
        activeDashboardId={activeDashboardId}
        dashboardNavItems={dashboardNavItems}
        onClose={() => setAllOpen(false)}
        onNavigateToDashboard={(dashboardId) => {
          if (navigationLocked) {
            return;
          }
          const slug = navItems.find(
            (item) => item.dashboardId === dashboardId
          )?.slug;
          if (slug == null) {
            void setActiveDashboardById(dashboardId);
            return;
          }
          requestNavigateToSlug(slug);
        }}
        opened={allOpen}
        sourcePageId={sourcePageId}
      />
      <DashboardUnsavedChangesModal
        opened={pendingSlug != null}
        onStay={() => setPendingSlug(null)}
        onDiscard={() => {
          const slug = pendingSlug;
          setPendingSlug(null);
          exitDashboardEdit();
          if (slug != null) {
            navigateToSlug(slug);
          }
        }}
      />
    </>
  );
}
